import {
  Box,
  Card,
  Chip,
  alpha,
  Alert,
  Button,
  Tooltip,
  useTheme,
  Checkbox,
  TextField,
  Typography,
  IconButton,
  CardContent,
  DialogTitle,
  ToggleButton,
  DialogActions,
  DialogContent,
  Grid2 as Grid,
  InputAdornment,
  ToggleButtonGroup,
} from '@mui/material';
import ProductCompanyStatusChip, {
  ProductCompanyStatusSummary,
  getProductCompanyStatusLabel,
  getProductCompanyStatusVisual,
} from 'components/product-company-status-chip';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ResponsiveDialog from 'components/responsive-dialog';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { BookCompany, ProjectCompanyCode } from 'library/types';
import { PRODUCT_COMPANY_STATUS_DISPLAY_ORDER, PRODUCT_COMPANY_STATUS_TYPE_ENUM } from 'enums';
import { renderFormattedDate, normalizeProjectCompanyCodes, getProjectCompanyCodeStatusCounts } from 'utils';

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/ı/g, 'i');

type ProductCompanyCodesDialogProps = {
  open: boolean;
  onClose: () => void;
  companyCodes: readonly ProjectCompanyCode[];
  allCompanies: readonly BookCompany[];
  productTitle?: string;
  editable?: boolean;
  searchable?: boolean;
  onSave?: (updatedCompanyCodes: ProjectCompanyCode[]) => void;
};

type CompanyWithStatus = BookCompany & {
  companyStatus: PRODUCT_COMPANY_STATUS_TYPE_ENUM;
};

const ProductCompanyCodesDialog = ({
  open,
  onClose,
  companyCodes = [],
  allCompanies = [],
  productTitle,
  editable = false,
  searchable,
  onSave,
}: ProductCompanyCodesDialogProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const showSearch = searchable ?? editable;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCompanyCodes, setSelectedCompanyCodes] = useState<ProjectCompanyCode[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedCompanyCodes(normalizeProjectCompanyCodes(companyCodes));
      setSearchQuery('');
    }
  }, [open, companyCodes]);

  const handleToggleCode = (code: string) => {
    setSelectedCompanyCodes((previousCodes) =>
      previousCodes.some((item) => item.companyCode === code)
        ? previousCodes.filter((item) => item.companyCode !== code)
        : [...previousCodes, { companyCode: code, status: PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE }]
    );
  };

  const handleStatusChange = (companyCode: string, status: PRODUCT_COMPANY_STATUS_TYPE_ENUM) => {
    setSelectedCompanyCodes((previousCodes) =>
      previousCodes.map((item) => (item.companyCode === companyCode ? { ...item, status } : item))
    );
  };

  const handleSelectAll = () => {
    setSelectedCompanyCodes((previousCodes) => {
      const selectedByCode = new Map(previousCodes.map((item) => [item.companyCode, item]));

      allCompanies.forEach(({ code }) => {
        if (code && !selectedByCode.has(code)) {
          selectedByCode.set(code, { companyCode: code, status: PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE });
        }
      });

      return Array.from(selectedByCode.values());
    });
  };

  const handleClearAll = () => {
    setSelectedCompanyCodes([]);
  };

  const handleCommitSave = () => {
    if (onSave) {
      onSave(normalizeProjectCompanyCodes(selectedCompanyCodes));
    }
    onClose();
  };

  const matchedCompaniesForView = useMemo(() => {
    const companiesByCode = new Map(allCompanies.map((company) => [company.code, company]));

    return normalizeProjectCompanyCodes(companyCodes).map(({ companyCode, status }) => {
      const company = companiesByCode.get(companyCode);

      return {
        ...(company ?? { code: companyCode, vkn: '-', title: companyCode, dateOfAnnulment: null }),
        companyStatus: status,
      };
    });
  }, [companyCodes, allCompanies]);

  const displayedCompanies = useMemo<CompanyWithStatus[]>(() => {
    if (!editable) return matchedCompaniesForView;

    const statusByCode = new Map(selectedCompanyCodes.map(({ companyCode, status }) => [companyCode, status]));

    return allCompanies.map((company) => ({
      ...company,
      companyStatus: statusByCode.get(company.code) ?? PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE,
    }));
  }, [allCompanies, editable, matchedCompaniesForView, selectedCompanyCodes]);

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = normalizeSearchText(searchQuery.trim());

    if (!showSearch || !normalizedQuery) return displayedCompanies;

    return displayedCompanies.filter((company) =>
      [company.code, company.title, company.vkn].some((value) =>
        normalizeSearchText(value ?? '').includes(normalizedQuery)
      )
    );
  }, [displayedCompanies, searchQuery, showSearch]);

  const normalizedCompanyCodes = useMemo(() => normalizeProjectCompanyCodes(companyCodes), [companyCodes]);
  const codesToSummarize = editable ? selectedCompanyCodes : normalizedCompanyCodes;
  const displayedCodeCount = codesToSummarize.length;
  const statusCounts = useMemo(() => getProjectCompanyCodeStatusCounts(codesToSummarize), [codesToSummarize]);
  const statusOptions = useMemo(
    () =>
      PRODUCT_COMPANY_STATUS_DISPLAY_ORDER.map((status) => ({
        status,
        label: getProductCompanyStatusLabel(status, intl),
        visual: getProductCompanyStatusVisual(status, theme),
      })),
    [intl, theme]
  );

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          pb: 1.5,
        }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}>
            <BusinessIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              <FormattedMessage id="project.table.companyCodesModalTitle" defaultMessage="Ürüne Bağlı Şirketler" />
            </Typography>
            {productTitle && (
              <Typography variant="caption" color="text.secondary">
                {productTitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.75} flexWrap="wrap">
          <Chip
            label={
              editable
                ? intl.formatMessage(
                    { id: 'project.table.selectedCompaniesCount', defaultMessage: '{count} Şirket Seçili' },
                    { count: displayedCodeCount }
                  )
                : `${displayedCodeCount} ${intl.formatMessage({ id: 'project.table.code', defaultMessage: 'Şirket' })}`
            }
            color={displayedCodeCount > 0 ? 'primary' : 'default'}
            variant={displayedCodeCount > 0 ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: 700 }}
          />

          {displayedCodeCount > 0 && <ProductCompanyStatusSummary counts={statusCounts} />}

          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {editable && allCompanies.length === 0 ? (
          <Alert severity="warning" icon={<InfoOutlinedIcon fontSize="inherit" />}>
            <FormattedMessage
              id="project.table.noAvailableCompanies"
              defaultMessage="Henüz tanımlı bir şirket bulunmuyor. Lütfen önce yukarıdaki tablodan şirket ekleyiniz."
            />
          </Alert>
        ) : !editable && matchedCompaniesForView.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">
              <FormattedMessage id="project.table.noCompanyCodes" defaultMessage="Tanımlı şirket kodu yok" />
            </Typography>
          </Box>
        ) : (
          <>
            {(showSearch || editable) && (
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                {showSearch && (
                  <TextField
                    fullWidth
                    autoFocus={!editable}
                    size="small"
                    placeholder={intl.formatMessage({
                      id: 'project.table.searchCompany',
                      defaultMessage: 'Şirket ara (Kod, Ünvan, VKN)...',
                    })}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    inputProps={{
                      'aria-label': intl.formatMessage({
                        id: 'project.table.searchCompany',
                        defaultMessage: 'Şirket ara (Kod, Ünvan, VKN)...',
                      }),
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                          <Tooltip
                            title={intl.formatMessage({
                              id: 'project.table.clearSearch',
                              defaultMessage: 'Aramayı temizle',
                            })}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => setSearchQuery('')}
                              aria-label={intl.formatMessage({
                                id: 'project.table.clearSearch',
                                defaultMessage: 'Aramayı temizle',
                              })}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                )}

                {editable && (
                  <Box display="flex" gap={0.5}>
                    <Tooltip title={<FormattedMessage id="project.table.selectAll" defaultMessage="Tümünü Seç" />}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SelectAllIcon />}
                        onClick={handleSelectAll}
                        sx={{ height: 40, whiteSpace: 'nowrap', textTransform: 'none' }}>
                        <FormattedMessage id="project.table.selectAll" defaultMessage="Tümünü Seç" />
                      </Button>
                    </Tooltip>
                    <Tooltip title={<FormattedMessage id="project.table.clearSelection" defaultMessage="Temizle" />}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<ClearAllIcon />}
                          onClick={handleClearAll}
                          disabled={selectedCompanyCodes.length === 0}
                          sx={{ height: 40, whiteSpace: 'nowrap', textTransform: 'none' }}>
                          <FormattedMessage id="project.table.clearSelection" defaultMessage="Temizle" />
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}

            {filteredCompanies.length === 0 ? (
              <Box py={5} textAlign="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    mx: 'auto',
                    mb: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    color: 'text.secondary',
                    bgcolor: alpha(theme.palette.text.secondary, 0.08),
                  }}>
                  <SearchOffIcon />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  <FormattedMessage
                    id="project.table.noCompanySearchResults"
                    defaultMessage="Aramanızla eşleşen şirket bulunamadı."
                  />
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                <Grid container spacing={1.5}>
                  {filteredCompanies.map((company) => {
                    const isSelected = selectedCompanyCodes.some((item) => item.companyCode === company.code);

                    return editable ? (
                      <Grid size={{ xs: 12 }} key={company.code}>
                        <Card
                          variant="outlined"
                          onClick={() => handleToggleCode(company.code)}
                          sx={{
                            borderRadius: 2,
                            cursor: 'pointer',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            bgcolor: isSelected
                              ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05)
                              : 'background.paper',
                            boxShadow: isSelected ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}` : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                            },
                          }}>
                          <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                            <Box
                              display="flex"
                              flexDirection={{ xs: 'column', sm: 'row' }}
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                              justifyContent="space-between"
                              gap={1}>
                              <Box display="flex" alignItems="center" gap={1.5} flex={1} overflow="hidden">
                                <Checkbox
                                  checked={isSelected}
                                  color="primary"
                                  size="small"
                                  sx={{ p: 0.5 }}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={() => handleToggleCode(company.code)}
                                  inputProps={{
                                    'aria-label': `${company.code} ${company.title || ''}`.trim(),
                                  }}
                                />
                                <Box overflow="hidden">
                                  <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                                    <Chip
                                      label={company.code}
                                      size="small"
                                      color={isSelected ? 'primary' : 'default'}
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      sx={{ fontWeight: 700, fontSize: '0.75rem', height: 22 }}
                                    />
                                    <Typography variant="subtitle2" fontWeight={700} noWrap color="text.primary">
                                      {company.title || company.code}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" gap={2} flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary">
                                      <strong>VKN:</strong> {company.vkn || '-'}
                                    </Typography>
                                    {company.dateOfAnnulment && (
                                      <Typography variant="caption" color="error.main">
                                        <strong>
                                          <FormattedMessage
                                            id="project.table.dateOfAnnulment"
                                            defaultMessage="Fesih Tarihi"
                                          />
                                          :
                                        </strong>{' '}
                                        {renderFormattedDate(company.dateOfAnnulment)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>

                              {isSelected && (
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={company.companyStatus}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(_, status: PRODUCT_COMPANY_STATUS_TYPE_ENUM | null) => {
                                    if (status !== null) handleStatusChange(company.code, status);
                                  }}
                                  aria-label={intl.formatMessage(
                                    {
                                      id: 'project.table.companyStatusFor',
                                      defaultMessage: '{companyCode} şirket durumu',
                                    },
                                    { companyCode: company.code }
                                  )}
                                  sx={{
                                    flexShrink: 1,
                                    alignSelf: { xs: 'flex-end', sm: 'center' },
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                  }}>
                                  {statusOptions.map(({ status, label, visual }) => (
                                    <ToggleButton
                                      key={status}
                                      value={status}
                                      aria-label={label}
                                      sx={{
                                        gap: 0.5,
                                        px: 1,
                                        py: 0.5,
                                        fontSize: '0.72rem',
                                        textTransform: 'none',
                                        '&.Mui-selected': {
                                          color: visual.color,
                                          bgcolor: visual.backgroundColor,
                                        },
                                      }}>
                                      {visual.icon}
                                      {label}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ) : (
                      <Grid size={{ xs: 12 }} key={company.code}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: theme.shadows[1],
                            },
                          }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={1}
                              mb={0.75}>
                              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                                {company.title || company.code}
                              </Typography>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                flexWrap="wrap"
                                justifyContent="flex-end">
                                <Chip
                                  label={company.code}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }}
                                />
                                <ProductCompanyStatusChip
                                  status={company.companyStatus}
                                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }}
                                />
                              </Box>
                            </Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                              <Typography variant="caption" color="text.secondary">
                                <strong>VKN:</strong> {company.vkn || '-'}
                              </Typography>
                              {company.dateOfAnnulment && (
                                <Typography variant="caption" color="error.main">
                                  <strong>
                                    <FormattedMessage
                                      id="project.table.dateOfAnnulment"
                                      defaultMessage="Fesih Tarihi"
                                    />
                                    :
                                  </strong>{' '}
                                  {renderFormattedDate(company.dateOfAnnulment)}
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: editable ? 'space-between' : 'flex-end' }}>
        {editable ? (
          <>
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage
                id="project.table.selectedCompaniesCount"
                defaultMessage="{count} Şirket Seçili"
                values={{ count: selectedCompanyCodes.length }}
              />
            </Typography>
            <Box display="flex" gap={1}>
              <Button onClick={onClose} variant="outlined" color="inherit" size="small">
                <FormattedMessage id="dashboard.cancel" defaultMessage="Vazgeç" />
              </Button>
              <Button onClick={handleCommitSave} variant="contained" color="primary" size="small">
                <FormattedMessage id="dashboard.save" defaultMessage="Kaydet" />
              </Button>
            </Box>
          </>
        ) : (
          <Button onClick={onClose} variant="contained" size="small">
            <FormattedMessage id="dashboard.okButtonLabel" defaultMessage="Tamam" />
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ProductCompanyCodesDialog;
